import type {
  BucketKey,
  IDailyStat,
  IAggregateResult,
  IAggregatedEventStats,
  IAggregatedSessionStats,
  IDailyEventStats,
} from './types';

const BUCKETS: BucketKey[] = ['fast', 'medium', 'slow', 'extraSlow'];
const EVENT_TYPE_KEYS = ['full', 'color', 'history', 'unknown'] as const;

function pct(part: number, whole: number): number {
  if (!whole) return 0;
  return (part / whole) * 100;
}

function bucketShare(c: Record<BucketKey, number>): Record<BucketKey, number> {
  const tot = c.fast + c.medium + c.slow + c.extraSlow;
  if (!tot) return { fast: 0, medium: 0, slow: 0, extraSlow: 0 };
  return {
    fast: (100 * c.fast) / tot,
    medium: (100 * c.medium) / tot,
    slow: (100 * c.slow) / tot,
    extraSlow: (100 * c.extraSlow) / tot,
  };
}

function bucketAvg(sums: Record<BucketKey, number>, counts: Record<BucketKey, number>): Record<BucketKey, number> {
  const r = {} as Record<BucketKey, number>;
  BUCKETS.forEach((b) => {
    r[b] = counts[b] ? sums[b] / counts[b] : 0;
  });
  return r;
}

function num(v: unknown): number {
  if (v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function aggregate(daily: IDailyStat[], start: string, end: string): IAggregateResult | null {
  const filtered = daily.filter((d) => d.statDate >= start && d.statDate <= end);
  if (filtered.length === 0) return null;

  const ss: IAggregatedSessionStats = {
    totalSessions: 0,
    uniqueCustomers: 0,
    sessionsWithEvents: 0,
    sessionsWithoutEvents: 0,
    sessionsWithAddToCart: 0,
    sessionsWithOrders: 0,
  };

  const es = {
    eventCount: 0,
    success: 0,
    error: 0,
    notEnded: 0,
    errMockup: 0,
    errRemovebg: 0,
    errNetwork: 0,
    leftBefore10s: 0,
    leftAfter10s: 0,
    leftAfter20s: 0,
    procSum: 0,
    procCount: 0,
    mockSum: 0,
    mockCount: 0,
    actualMockSum: 0,
    actualMockCount: 0,
    saveSum: 0,
    saveCount: 0,
    procBucketCount: { fast: 0, medium: 0, slow: 0, extraSlow: 0 } as Record<BucketKey, number>,
    procBucketSum: { fast: 0, medium: 0, slow: 0, extraSlow: 0 } as Record<BucketKey, number>,
    mockBucketCount: { fast: 0, medium: 0, slow: 0, extraSlow: 0 } as Record<BucketKey, number>,
    mockBucketSum: { fast: 0, medium: 0, slow: 0, extraSlow: 0 } as Record<BucketKey, number>,
    actualMockBucketCount: { fast: 0, medium: 0, slow: 0, extraSlow: 0 } as Record<BucketKey, number>,
    actualMockBucketSum: { fast: 0, medium: 0, slow: 0, extraSlow: 0 } as Record<BucketKey, number>,
    saveBucketCount: { fast: 0, medium: 0, slow: 0, extraSlow: 0 } as Record<BucketKey, number>,
    saveBucketSum: { fast: 0, medium: 0, slow: 0, extraSlow: 0 } as Record<BucketKey, number>,
    eventTypes: { full: 0, color: 0, history: 0, unknown: 0 },
  };

  filtered.forEach((d) => {
    const s = d.sessionStats[0];
    if (!s) return;
    (Object.keys(ss) as (keyof IAggregatedSessionStats)[]).forEach((k) => {
      ss[k] += num(s[k]);
    });
    const e = d.eventStats as IDailyEventStats;
    es.eventCount += num(e.eventCount);
    es.success += num(e.endStatusCountCat?.success);
    es.error += num(e.endStatusCountCat?.error);
    es.notEnded += num(e.endStatusCountCat?.notEnded);
    const r = e.raw || {};
    if (r.errorCounts) {
      es.errMockup += num(r.errorCounts.ERR_MOCKUP);
      es.errRemovebg += num(r.errorCounts.ERR_REMOVEBG);
      es.errNetwork += num(r.errorCounts.ERR_NETWORK);
    } else {
      const errTotal = num(e.endStatusCountCat?.error);
      es.errMockup += Math.round((errTotal * num(e.endStatusErrorPct?.errMockup)) / 100);
      es.errRemovebg += Math.round((errTotal * num(e.endStatusErrorPct?.errRemovebg)) / 100);
      es.errNetwork += Math.round((errTotal * num(e.endStatusErrorPct?.errNetwork)) / 100);
    }
    if (r.leftBefore10sCount !== undefined) {
      es.leftBefore10s += num(r.leftBefore10sCount);
      es.leftAfter10s += num(r.leftAfter10sCount);
      es.leftAfter20s += num(r.leftAfter20sCount);
    } else {
      const neTotal = num(e.endStatusCountCat?.notEnded);
      es.leftBefore10s += Math.round((neTotal * num(e.endStatusNotEndedCat?.leftBefore10sPct)) / 100);
      es.leftAfter10s += Math.round((neTotal * num(e.endStatusNotEndedCat?.leftAfter10sPct)) / 100);
      es.leftAfter20s += Math.round((neTotal * num(e.endStatusNotEndedCat?.leftAfter20sPct)) / 100);
    }
    if (r.totalProcessDurationForAvg !== undefined && r.validProcessDurationCountForAvg) {
      es.procSum += num(r.totalProcessDurationForAvg);
      es.procCount += num(r.validProcessDurationCountForAvg);
    } else {
      es.procSum += num(e.processDurationAvg) * num(e.validProcessCount);
      es.procCount += num(e.validProcessCount);
    }
    if (r.totalMockupDurationForAvg !== undefined && r.validMockupDurationCountForAvg) {
      es.mockSum += num(r.totalMockupDurationForAvg);
      es.mockCount += num(r.validMockupDurationCountForAvg);
    } else {
      es.mockSum += num(e.mockupDurationAvg) * num(e.validMockupCount);
      es.mockCount += num(e.validMockupCount);
    }
    if (r.totalActualMockupDurationForAvg !== undefined && r.validActualMockupDurationCountForAvg) {
      es.actualMockSum += num(r.totalActualMockupDurationForAvg);
      es.actualMockCount += num(r.validActualMockupDurationCountForAvg);
    } else {
      es.actualMockSum += num(e.actualMockupDurationAvg) * num(e.validActualMockupCount);
      es.actualMockCount += num(e.validActualMockupCount);
    }
    if (r.totalsaveDurationForAvg !== undefined && r.validsaveDurationCountForAvg) {
      es.saveSum += num(r.totalsaveDurationForAvg);
      es.saveCount += num(r.validsaveDurationCountForAvg);
    } else {
      es.saveSum += num(e.saveDurationAvg) * num(e.validsaveCount);
      es.saveCount += num(e.validsaveCount);
    }
    BUCKETS.forEach((b) => {
      if (r.processDurationCounts) es.procBucketCount[b] += num(r.processDurationCounts[b]);
      if (r.processDurationSums) es.procBucketSum[b] += num(r.processDurationSums[b]);
      if (r.mockupDurationCounts) es.mockBucketCount[b] += num(r.mockupDurationCounts[b]);
      if (r.mockupDurationSums) es.mockBucketSum[b] += num(r.mockupDurationSums[b]);
      if (r.actualMockupDurationCounts) es.actualMockBucketCount[b] += num(r.actualMockupDurationCounts[b]);
      if (r.actualMockupDurationSums) es.actualMockBucketSum[b] += num(r.actualMockupDurationSums[b]);
      if (r.saveDurationCounts) es.saveBucketCount[b] += num(r.saveDurationCounts[b]);
      if (r.saveDurationSums) es.saveBucketSum[b] += num(r.saveDurationSums[b]);
    });
    EVENT_TYPE_KEYS.forEach((k) => {
      es.eventTypes[k] += num(e.eventTypes?.[k]);
    });
  });

  const successPct = pct(es.success, es.eventCount);
  const errorPct = pct(es.error, es.eventCount);
  const notEndedPct = pct(es.notEnded, es.eventCount);
  const errTot = es.error || 1;
  const neTot = es.notEnded || 1;

  const eventStats: IAggregatedEventStats = {
    eventCount: es.eventCount,
    success: es.success,
    error: es.error,
    notEnded: es.notEnded,
    successPct,
    errorPct,
    notEndedPct,
    errMockup: es.errMockup,
    errRemovebg: es.errRemovebg,
    errNetwork: es.errNetwork,
    errMockupPct: pct(es.errMockup, errTot),
    errRemovebgPct: pct(es.errRemovebg, errTot),
    errNetworkPct: pct(es.errNetwork, errTot),
    leftBefore10s: es.leftBefore10s,
    leftAfter10s: es.leftAfter10s,
    leftAfter20s: es.leftAfter20s,
    leftBefore10sPct: pct(es.leftBefore10s, neTot),
    leftAfter10sPct: pct(es.leftAfter10s, neTot),
    leftAfter20sPct: pct(es.leftAfter20s, neTot),
    processDurationAvg: es.procCount ? es.procSum / es.procCount : 0,
    mockupDurationAvg: es.mockCount ? es.mockSum / es.mockCount : 0,
    actualMockupDurationAvg: es.actualMockCount ? es.actualMockSum / es.actualMockCount : 0,
    saveDurationAvg: es.saveCount ? es.saveSum / es.saveCount : 0,
    processCat: bucketShare(es.procBucketCount),
    actualMockupCat: bucketShare(es.actualMockBucketCount),
    mockupCat: bucketShare(es.mockBucketCount),
    saveCat: bucketShare(es.saveBucketCount),
    processBucketAvg: bucketAvg(es.procBucketSum, es.procBucketCount),
    mockupBucketAvg: bucketAvg(es.mockBucketSum, es.mockBucketCount),
    actualMockupBucketAvg: bucketAvg(es.actualMockBucketSum, es.actualMockBucketCount),
    saveBucketAvg: bucketAvg(es.saveBucketSum, es.saveBucketCount),
    eventTypes: es.eventTypes,
    addToCartPct: pct(ss.sessionsWithAddToCart, ss.totalSessions),
    eventsToCartPct: pct(ss.sessionsWithAddToCart, ss.sessionsWithEvents),
    cartToOrderPct: pct(ss.sessionsWithOrders, ss.sessionsWithAddToCart),
    orderPct: pct(ss.sessionsWithOrders, ss.totalSessions),
    sessionsWithEventsPct: pct(ss.sessionsWithEvents, ss.totalSessions),
  };

  return { sessionStats: ss, eventStats, days: filtered };
}
